import { Flex, Text, Tooltip } from "@mantine/core";
import { IconPresentationAnalytics, IconTrash } from "@tabler/icons-react";
import { memo, useCallback, useMemo } from "react";
import { useLocation } from "react-router-dom";
import ConfirmationButton from "src/components/shared/ConfirmationButton/ConfirmationButton";
import { DateTimeDisp } from "src/components/shared/DateTime";
import { useCheckPerm, useIsOnline } from "src/hooks";
import { type IFile } from "src/interfaces";
import { StyledActionIcon, StyledTd, StyledTr } from "src/styled";

import { useFileRow } from "./FileRow.hooks";

interface Props {
  row: IFile;
  readonly?: boolean;
  isMultipleAttachmentsModal?: boolean;
  overrideHandleRemove?: (filename: string) => void;
  customHandleRemove?: (attachmentId: string) => Promise<void>;
}

const FileRow = ({
  row: file,
  readonly,
  isMultipleAttachmentsModal,
  overrideHandleRemove,
  customHandleRemove,
}: Props) => {
  const isOnline = useIsOnline();
  const checkPerm = useCheckPerm();
  const { pathname } = useLocation();
  const { isLoading, isNewEntry, handleOpen, handleRemove } = useFileRow(file);
  const filename = file.filename ?? file.name ?? "";
  const isReview = useMemo(
    () => pathname.includes("summary/reviews/review/"),
    [pathname],
  );
  const handleRemoveFile = async () => {
    if (overrideHandleRemove) {
      overrideHandleRemove(filename);
    } else if (customHandleRemove && file.aid) {
      await customHandleRemove(String(file.aid));
    } else {
      void handleRemove();
    }
  };
  const renderActions = useCallback(() => {
    if (file.deleted) {
      return <Text c="#ff6b6b">Removed</Text>;
    }

    if (file.local) {
      return <Text c="orange">Pending</Text>;
    }

    if (!isOnline) {
      return <></>;
    }

    return (
      <>
        {checkPerm("downloadfiles") && !isMultipleAttachmentsModal && (
          <Tooltip label="Open">
            <StyledActionIcon
              onClick={() => handleOpen(file)}
              loading={isLoading}>
              <IconPresentationAnalytics />
            </StyledActionIcon>
          </Tooltip>
        )}
        {checkPerm("deletefiles") && !readonly && !isReview && file.aid && (
          <ConfirmationButton
            actionIcon
            onConfirm={handleRemoveFile}
            modalText={`Are you sure you want to remove ${filename}?`}>
            <Tooltip label="Remove">
              <IconTrash color="#ff6b6b" />
            </Tooltip>
          </ConfirmationButton>
        )}
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps -- deps kept as they were; changing them changes when this runs
  }, [
    checkPerm,
    file.deleted,
    filename,
    file.local,
    handleOpen,
    handleRemove,
    isLoading,
    isOnline,
    isReview,
    readonly,
  ]);

  if (isNewEntry) {
    return (
      <StyledTr>
        <StyledTd>{filename}</StyledTd>
      </StyledTr>
    );
  }
  return (
    <StyledTr>
      <StyledTd>{filename}</StyledTd>
      <StyledTd center>
        <DateTimeDisp
          dir="row"
          utc={file.dateUTC}
          lt={file.date}
          containerProps={{ style: { justifyContent: "center" } }}
        />
      </StyledTd>
      <StyledTd center>{file.type}</StyledTd>
      <StyledTd>
        <Flex gap="lg" justify="center">
          {renderActions()}
        </Flex>
      </StyledTd>
    </StyledTr>
  );
};

export default memo(FileRow);
